import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SectionLabel } from '@/components/px/SectionLabel'
import { useAppDispatch, useAppSelector } from '@/store'
import { cn } from '@/lib/utils'

import {
  fetchQuestionnaire,
  saveQuestionnaireAnswers,
  selectModuleMutationError
} from './moduleCatalogueSlice'
import { useUnsavedAnswerGuard } from './useUnsavedAnswerGuard'
import type { ModuleDefinition, ModuleQuestion, ProjectModule } from './types'

const inputClass =
  'rounded-md border border-input bg-card px-2 py-1.5 text-sm text-foreground read-only:text-muted-foreground'

/** The typed default for an unanswered question, by its wire `type`. */
const defaultAnswerFor = (question: ModuleQuestion): unknown => {
  switch (question.type) {
    case 'boolean':
      return false
    case 'multi-select':
      return [] as string[]
    default:
      return ''
  }
}

/**
 * One question's control, rendered by the wire `type` (AC-2): `string` →
 * input, `text` → textarea, `number` → number input, `boolean` → checkbox,
 * `single-select` → radio group, `multi-select` → checkbox group, `file-ref`
 * → read-only note naming the accepted reference format (no file upload route
 * exists on this surface).
 */
function QuestionControl({
  question,
  value,
  onValueChanged
}: {
  question: ModuleQuestion
  value: unknown
  onValueChanged: (next: unknown) => void
}) {
  const { t } = useTranslation('modules')
  const { id, type, options = [] } = question

  if (type === 'boolean') {
    const checked = value === true
    return (
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => onValueChanged(event.target.checked)}
        className="h-4 w-4 accent-[var(--color-primary)]"
      />
    )
  }

  if (type === 'single-select') {
    return (
      <div role="radiogroup" aria-labelledby={`${id}-label`} className="mt-1 flex flex-col gap-1">
        {options.map((option) => (
          <label key={option} className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="radio"
              name={id}
              value={option}
              checked={value === option}
              onChange={() => onValueChanged(option)}
              className="h-4 w-4 accent-[var(--color-primary)]"
            />
            {option}
          </label>
        ))}
      </div>
    )
  }

  if (type === 'multi-select') {
    const selected = Array.isArray(value) ? (value as unknown[]) : []
    return (
      <div className="mt-1 flex flex-col gap-1">
        {options.map((option) => (
          <label key={option} className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={(event) => {
                const next = event.target.checked
                  ? [...selected, option]
                  : selected.filter((item) => item !== option)
                onValueChanged(next)
              }}
              className="h-4 w-4 accent-[var(--color-primary)]"
            />
            {option}
          </label>
        ))}
      </div>
    )
  }

  if (type === 'file-ref') {
    return (
      <p className="mt-1 text-sm text-muted-foreground" id={id}>
        {t('questionnaire.fileRefNote')}
      </p>
    )
  }

  if (type === 'text') {
    return (
      <textarea
        id={id}
        rows={3}
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => onValueChanged(event.target.value)}
        className={`${inputClass} w-full max-w-lg`}
      />
    )
  }

  return (
    <input
      id={id}
      type={type === 'number' ? 'number' : 'text'}
      value={value === undefined || value === null ? '' : String(value)}
      onChange={(event) => onValueChanged(event.target.value)}
      className={`${inputClass} w-full max-w-lg`}
    />
  )
}

export interface QuestionnaireDrawerProps {
  definition: ModuleDefinition
  module: ProjectModule
  projectId: string
  onClose: () => void
}

/**
 * The questionnaire drawer (PX-W6.5, AC-2): fetches the module's
 * questionnaire by its lower-kebab key, pre-fills the controls from the
 * attachment row's `userAnswers`, holds edited answers in LOCAL component
 * state until save (INV-9 — Redux receives only the saved result), and guards
 * dirty navigation through {@link useUnsavedAnswerGuard}.
 *
 * Save sends the FULL answer map (the server replaces wholesale), and the
 * drawer closes only on success.
 */
export function QuestionnaireDrawer({
  definition,
  module,
  projectId,
  onClose
}: QuestionnaireDrawerProps) {
  const { t } = useTranslation(['modules', 'common'])
  const dispatch = useAppDispatch()

  const storedQuestionnaire = useAppSelector((s) => s.modules.questionnaires[definition.moduleId])
  const saving = useAppSelector((s) => s.modules.savingAnswers)
  const mutationError = useAppSelector(selectModuleMutationError)

  const [answers, setAnswers] = useState<Record<string, unknown>>({ ...module.userAnswers })

  useEffect(() => {
    void dispatch(fetchQuestionnaire(definition.moduleId))
  }, [dispatch, definition.moduleId])

  const questions = useMemo(() => {
    const source =
      storedQuestionnaire?.questions ?? definition.questionnaire?.questions ?? []
    return source
  }, [storedQuestionnaire, definition.questionnaire])

  const dirty = useMemo(
    () => JSON.stringify(answers) !== JSON.stringify(module.userAnswers),
    [answers, module.userAnswers]
  )

  const guard = useUnsavedAnswerGuard(dirty)

  const [dialogOpen, setDialogOpen] = useState(false)

  const onAnswerChanged = (question: ModuleQuestion, next: unknown): void => {
    setAnswers((current) => ({ ...current, [question.id]: next }))
  }

  /** Close routes through the guard when dirty; a clean drawer just closes. */
  const requestClose = (): void => {
    if (!dirty) {
      onClose()
      return
    }
    // Surface the SAME confirm dialog the navigation guard uses: closing with
    // unsaved answers is a discard, and must be an explicit act.
    setDialogOpen(true)
  }

  const onSaveClicked = (): void => {
    void dispatch(
      saveQuestionnaireAnswers({
        projectId,
        moduleId: module.id,
        // The server's PATCH sets `enabled` unconditionally from the body —
        // the current value must ride along or the save deactivates the module.
        enabled: module.enabled,
        userAnswers: answers
      })
    )
      .unwrap()
      .then(() => {
        // Success clears the dirty flag by definition — the saved row now
        // equals the edited map — so closing needs no second confirm.
        onClose()
      })
      .catch(() => {
        // The mapped mutationError renders below; the drawer stays open.
      })
  }

  return (
    <section
      aria-label={t('questionnaire.title', { name: definition.name })}
      className="rounded-lg border border-border bg-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          <SectionLabel>{t('questionnaire.heading')}</SectionLabel>
          <p className="mt-1 text-sm font-medium text-foreground">
            {t('questionnaire.title', { name: definition.name })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <p role="status" className="text-xs text-review">
              {t('questionnaire.unsaved')}
            </p>
          )}
          <button
            type="button"
            onClick={requestClose}
            className="rounded-md border border-input px-3 py-1.5 text-sm text-ink-soft hover:bg-muted"
          >
            {t('common:actionsMore.close')}
          </button>
          <button
            type="button"
            onClick={onSaveClicked}
            disabled={saving || !dirty}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {saving ? t('common:states.loading') : t('common:actionsMore.save')}
          </button>
        </div>
      </div>

      {storedQuestionnaire === undefined && definition.questionnaire === null ? (
        <p className="px-4 py-4 text-sm text-muted-foreground">{t('questionnaire.loading')}</p>
      ) : (
        <div className="px-4 py-4">
          {(storedQuestionnaire?.introduction ?? definition.questionnaire?.introduction) !==
            undefined && (
            <p className="max-w-2xl text-sm leading-relaxed text-ink-soft">
              {storedQuestionnaire?.introduction ?? definition.questionnaire?.introduction}
            </p>
          )}
          <ul className="mt-3 flex flex-col gap-4">
            {questions.map((question) => (
              <li key={question.id} className="border-t border-hairline pt-3 first:border-t-0 first:pt-0">
                <div id={`${question.id}-label`} className="flex items-baseline gap-1">
                  <label htmlFor={question.id} className="text-sm font-medium text-foreground">
                    {question.title}
                  </label>
                  {question.required && (
                    <>
                      <span aria-hidden className="text-sm font-semibold text-review">
                        *
                      </span>
                      <span className="sr-only">{t('questionnaire.required')}</span>
                    </>
                  )}
                </div>
                {question.criteria !== null && question.criteria !== undefined && (
                  <p className="mt-0.5 max-w-2xl text-xs text-muted-foreground">
                    {question.criteria}
                  </p>
                )}
                <QuestionControl
                  question={question}
                  value={answers[question.id] ?? defaultAnswerFor(question)}
                  onValueChanged={(next) => onAnswerChanged(question, next)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      {mutationError !== null && (
        <p role="alert" className="border-t border-border px-4 py-3 text-sm text-blocked">
          {t('questionnaire.saveFailed')}
        </p>
      )}

      {(guard.pendingHref !== null || dialogOpen) && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="questionnaire-discard-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 p-4"
        >
          <div className="w-full max-w-md rounded-lg border border-border bg-card px-4 py-4 shadow-lg">
            <h2 id="questionnaire-discard-title" className={cn('font-serif text-lg font-semibold')}>
              {t('questionnaire.discardTitle')}
            </h2>
            <p className="mt-1.5 text-sm text-ink-soft">{t('questionnaire.discardBody')}</p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setDialogOpen(false)
                  guard.keepEditing()
                }}
                className="rounded-md border border-input px-3 py-1.5 text-sm text-ink-soft hover:bg-muted"
              >
                {t('questionnaire.keepEditing')}
              </button>
              <button
                type="button"
                onClick={() => {
                  const to = guard.pendingHref
                  setDialogOpen(false)
                  if (to !== null) guard.confirmDiscard()
                  else onClose()
                }}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                {t('questionnaire.discard')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
